import { useCallback, useState } from "react";
import { toast } from "@/core/components/ui/toast";
import { extractErrorMessage } from "@/core/api/errors";
import { ApiError } from "@/core/api/types";
import { useCreatePayrollSheetApprovalAction } from "@/payroll/pay-periods/query";
import type {
  IPayrollSheetApprovalAccessKey,
  SheetApprovalType,
} from "@/payroll/payroll.d";
import { APPROVAL_INTENT, resolveApprovalIntent } from "./utils";

interface UseSheetApprovalActionsParams {
  businessId?: string | null;
  periodId?: string | null;
  runId?: string | null;
}

export interface ApprovalRemovalTarget {
  accessKey: IPayrollSheetApprovalAccessKey;
  approvalType: SheetApprovalType;
}

/**
 * Owns the complete interaction around changing a sheet approval: mutation
 * state, success/error feedback, and the confirmation required before removing
 * an existing sign-off.
 */
export const useSheetApprovalActions = ({
  businessId,
  periodId,
  runId,
}: UseSheetApprovalActionsParams) => {
  const [removalTarget, setRemovalTarget] =
    useState<ApprovalRemovalTarget | null>(null);
  const { mutate, isPending, variables } = useCreatePayrollSheetApprovalAction(
    businessId,
    periodId,
  );

  const submit = useCallback(
    (
      accessKey: IPayrollSheetApprovalAccessKey,
      approvalType: SheetApprovalType,
      enabled: boolean,
      reason?: string,
    ) => {
      if (!periodId || !runId) return;

      mutate(
        {
          access_key_id: accessKey.id,
          originating_ai_script_run_id: runId,
          approval_type: approvalType,
          enabled,
          ...(reason ? { reason } : {}),
        },
        {
          onSuccess: () => {
            setRemovalTarget(null);
            toast.success(enabled ? "Approval added" : "Approval removed", {
              description: accessKey.name,
            });
          },
          onError: (error) => {
            const { status, message } =
              error instanceof ApiError ? error : extractErrorMessage(error);
            const title =
              status === 403
                ? "You no longer have permission to approve this sheet"
                : status === 404
                  ? "This payroll result is no longer available"
                  : status === 409
                    ? "Payroll has been rerun"
                    : status === 422
                      ? "This approval could not be updated"
                      : "Failed to update approval";

            toast.error(title, { description: message });
          },
        },
      );
    },
    [mutate, periodId, runId],
  );

  const handleChange = useCallback(
    (
      accessKey: IPayrollSheetApprovalAccessKey,
      approvalType: SheetApprovalType,
      enabled: boolean,
    ) => {
      if (resolveApprovalIntent(enabled) === APPROVAL_INTENT.approve) {
        submit(accessKey, approvalType, true);
        return;
      }

      setRemovalTarget({ accessKey, approvalType });
    },
    [submit],
  );

  const confirmRemoval = useCallback(
    (reason?: string) => {
      if (!removalTarget) return;
      submit(
        removalTarget.accessKey,
        removalTarget.approvalType,
        false,
        reason,
      );
    },
    [removalTarget, submit],
  );

  const pendingApproval =
    isPending && variables
      ? {
          accessKeyId: variables.access_key_id,
          approvalType: variables.approval_type,
        }
      : null;

  return {
    confirmRemoval,
    handleChange,
    isPending,
    pendingApproval,
    removalTarget,
    setRemovalOpen: (open: boolean) => {
      if (!open) setRemovalTarget(null);
    },
  };
};
